import { useMemo } from 'react';
import { useAuth } from '@/providers/AuthProvider';
import { useTimesheet } from '@/contexts/TimesheetContext';
import { useCompany } from '@/features/multi-company/context/CompanyContext';
import { DEFAULT_RE_DISPLAY_AFTER_DAYS, useFeedbackConfig } from '@/providers/FeedbackConfigProvider';
import type { FeedbackCollectorParams } from '@/types/feedbackCollector';

/**
 * Custom hook to build the feedback collector component parameters.
 *
 * Sources userId from TimesheetContext, customerName from CompanyContext,
 * and feedback settings from FeedbackConfigProvider.
 *
 * @param feature - The feature name to collect feedback for (e.g., "Homepage", "Timesheet")
 * @returns The oc-component parameters, or null if config is not loaded yet
 */
export function useFeedbackCollectorParams(feature: string): FeedbackCollectorParams | null {
  const { currentEmployee } = useTimesheet();
  const { currentCompany } = useCompany();
  const { evoOrganisationId, instanceId } = useAuth();
  const config = useFeedbackConfig();

  // Derive only the primitive values we actually use so that unrelated
  // context re-renders (e.g. timesheet week navigation updating entries)
  // don't re-trigger the effect and cause the MFE to remount.
  const employeeId = currentEmployee?.id;
  const employeeName = currentEmployee?.name;
  const companyName = currentCompany?.name;

  const orgId = evoOrganisationId ?? instanceId ?? '';

  const accountToken = config?.accountToken;
  const apiHost = config?.apiHost;
  const reDisplayAfterDays = config?.reDisplayAfterDays ?? DEFAULT_RE_DISPLAY_AFTER_DAYS;
  const mfeRegistryBase = config?.mfeRegistryBase;
  const mfeVersion = config?.mfeVersion;
  const product = config?.product;

  return useMemo(() => {
    if (!accountToken || !apiHost || !mfeRegistryBase || !product) return null;

    return {
      feature,
      accountToken,
      apiHost,
      reDisplayAfterDays,
      mfeRegistryBase,
      mfeVersion,
      customerName: companyName || 'Unknown',
      userId: employeeId !== undefined ? String(employeeId) : employeeName || 'Unknown',
      orgId,
      product,
    };
  }, [
    accountToken,
    apiHost,
    companyName,
    employeeId,
    employeeName,
    feature,
    mfeRegistryBase,
    mfeVersion,
    orgId,
    product,
    reDisplayAfterDays,
  ]);
}
